/**
 * Application route constants
 * Centralized route definitions to avoid hardcoding paths throughout the application
 */

export const ROUTES = {
	// Root
	home: '/',
	dashboard: '/dashboard',

	// Authentication
	auth: {
		login: '/login',
		welcome: '/welcome',
		recovery: '/recovery',
	},

	// Finance
	finance: {
		root: '/finance',
		transactions: '/finance/transactions',
		// The overview and the reports are one page: the month is the first tab
		// and each report is reachable directly through ?tab=.
		reports: '/finance?tab=spending',
		create: '/finance/create',
		edit: (id: string) => `/finance/${id}/edit`,
		household: '/finance/household',
		categories: '/finance/categories',
		products: '/finance/products',
		rules: '/finance/rules',
	},

	// Shopping
	shopping: {
		root: '/shopping',
		view: (id: string) => `/shopping/${id}`,
	},

	// Journ
	journ: {
		root: '/journ',
		day: (day: string) => `/journ/${day}`,
		calendars: '/journ/calendars',
	},

	world: {
		root: '/world',
	},

	// Pets
	pets: {
		root: '/pets',
		create: '/pets/create',
		view: (id: string) => `/pets/${id}`,
		edit: (id: string) => `/pets/${id}/edit`,
		// The clinical record: what the animal carries, is on, has had done to it,
		// and what has been measured. One page since the medical history was folded
		// into it; each tab is reachable directly through the `?tab=` links below.
		health: (petId: string) => `/pets/${petId}/health`,
		procedures: {
			list: (petId: string) => `/pets/${petId}/health?tab=procedures`,
			create: (petId: string) => `/pets/${petId}/procedures/create`,
			edit: (petId: string, procedureId: string) => `/pets/${petId}/procedures/${procedureId}/edit`,
		},
		medications: {
			list: (petId: string) => `/pets/${petId}/health?tab=medications`,
			create: (petId: string) => `/pets/${petId}/medications/create`,
			edit: (petId: string, medicationId: string) => `/pets/${petId}/medications/${medicationId}/edit`,
		},
		conditions: {
			list: (petId: string) => `/pets/${petId}/health?tab=conditions`,
			create: (petId: string) => `/pets/${petId}/conditions/create`,
			edit: (petId: string, conditionId: string) => `/pets/${petId}/conditions/${conditionId}/edit`,
		},
		allergies: {
			list: (petId: string) => `/pets/${petId}/health?tab=allergies`,
			create: (petId: string) => `/pets/${petId}/allergies/create`,
			edit: (petId: string, allergyId: string) => `/pets/${petId}/allergies/${allergyId}/edit`,
		},
		vaccines: {
			list: (petId: string) => `/pets/${petId}/vaccines`,
			create: (petId: string) => `/pets/${petId}/vaccines/create`,
			edit: (petId: string, vaccineId: string) => `/pets/${petId}/vaccines/${vaccineId}/edit`,
		},
		photos: (petId: string) => `/pets/${petId}/photos`,
		tutors: (petId: string) => `/pets/${petId}/tutors`,
		vets: {
			list: '/pets/vets',
			create: '/pets/vets/create',
			edit: (vetId: string) => `/pets/vets/${vetId}/edit`,
		},
		clinics: {
			list: '/pets/clinics',
			create: '/pets/clinics/create',
			edit: (clinicId: string) => `/pets/clinics/${clinicId}/edit`,
		},
		exams: {
			list: (petId: string) => `/pets/${petId}/health?tab=exams`,
			create: (petId: string) => `/pets/${petId}/exams/create`,
			view: (petId: string, examId: string) => `/pets/${petId}/exams/${examId}`,
			edit: (petId: string, examId: string) => `/pets/${petId}/exams/${examId}/edit`,
		},
	},

	// Metrics
	metrics: {
		root: '/metrics',
	},

	pomodoro: {
		root: '/pomodoro',
	},

	exams: {
		root: '/exams',
		attempt: (examId: string) => `/exams/${examId}`,
	},

	// Public, token-gated medical-history share (no login required)
	share: {
		view: (token: string) => `/share/${token}`,
	},

	settings: {
		root: '/settings',
		general: '/settings/general',
		products: '/settings/products',
	},

	// User Profile
	profile: {
		root: '/profile',
		settings: '/settings',
		stats: '/profile/stats',
	},
} as const
